import { maximumAllowedMonitoredRolloutDurationMs } from '@gonfalon/dogfood-flags/dogfood-flags';
import { getMsFromDuration } from '@gonfalon/format/getDisplayDurationFromMs';
import { pluralize } from '@gonfalon/format/pluralize';
import { msg } from '@gonfalon/messages/msg';
import { MIN_ROLLOUT_WEIGHT } from '@gonfalon/releases/guarded-rollouts/constants';
import { z } from 'zod';

import type { ReleaseType } from '../types';
import { convertMsToDisplayDuration } from '../utils/convertMsToDisplayDuration';
import type { durationUnits } from '../utils/durationConstants';
import { getDurationQuantityLimits } from '../utils/getDurationQuantityLimits';

function createRolloutWeightSchema(releaseType: ReleaseType) {
  const schema = z
    // Only a missing value gets this copy; any other non-number falls back to zod's own message.
    .number({
      error: (issue) =>
        issue.input === undefined ? msg('guardedRollouts.stepValidation.rolloutWeightRequired') : undefined,
    })
    .int({ error: () => msg('guardedRollouts.stepValidation.rolloutWeightInteger') })
    .min(MIN_ROLLOUT_WEIGHT, { error: () => msg('guardedRollouts.stepValidation.rolloutWeightMin') });

  switch (releaseType) {
    case 'guarded':
      return schema.lte(50000, { error: () => msg('guardedRollouts.stepValidation.rolloutWeightGuardedMax') });
    case 'progressive':
      return schema.lt(100000, 'Decrease rollout percentage to less than 100%');
    default:
      return schema.lt(100000, 'Decrease rollout percentage to less than 100%');
  }
}

function createDurationSchema<T extends (typeof durationUnits)[number]>(unit: T) {
  const limits = getDurationQuantityLimits()[unit];
  const pluralUnit = unit === 'day' ? 'days' : unit === 'hour' ? 'hours' : 'minutes';

  let quantitySchema = z
    // A missing value keeps the generic "Required" copy; anything else that is not a number,
    // such as a cleared number input, gets the duration-specific copy.
    .number({
      error: (issue) =>
        issue.input === undefined
          ? msg('guardedRollouts.stepValidation.durationRequired')
          : msg('guardedRollouts.stepValidation.durationInvalid'),
    })
    .min(limits.min, {
      error: () =>
        msg('guardedRollouts.stepValidation.durationMin', {
          quantity: limits.min,
          unit: pluralize(limits.min, unit, pluralUnit) ?? pluralUnit,
        }),
    });

  if (limits.max !== undefined) {
    quantitySchema = quantitySchema.max(limits.max, {
      error: () =>
        msg('guardedRollouts.stepValidation.durationMax', {
          quantity: limits.max as number,
          unit: pluralize(limits.max as number, unit, pluralUnit) ?? pluralUnit,
        }),
    });
  }

  return z.object({
    quantity: quantitySchema,
    unit: z.literal(unit),
  });
}

function createProgressiveReleasePhaseConfigurationStepSchema(releaseType: ReleaseType) {
  return z.object({
    key: z.string().min(1, { error: () => msg('guardedRollouts.stepValidation.keyRequired') }),
    rolloutWeight: createRolloutWeightSchema(releaseType),
    // Keying the union on the unit keeps duration problems attached to the duration.quantity and
    // duration.unit paths, which is where the stage error list looks for them.
    duration: z.discriminatedUnion('unit', [
      createDurationSchema('day'),
      createDurationSchema('hour'),
      createDurationSchema('minute'),
    ]),
  });
}

export const progressiveReleasePhaseConfigurationStepSchema =
  createProgressiveReleasePhaseConfigurationStepSchema('progressive');

export type ProgressiveReleasePhaseConfigurationStep = z.infer<typeof progressiveReleasePhaseConfigurationStepSchema>;

function createProgressiveReleasePhaseConfigurationStepsSchema(releaseType: ReleaseType) {
  return z.array(createProgressiveReleasePhaseConfigurationStepSchema(releaseType)).superRefine((steps, ctx) => {
    // Validate that rollout weights are increasing
    toNumberPairs(steps.map((step) => step.rolloutWeight)).forEach(([a, b], aIndex) => {
      if (b <= a) {
        ctx.addIssue({
          code: 'custom',
          message: msg('guardedRollouts.stepValidation.rolloutWeightIncreasing'),
          path: [aIndex + 1, 'rolloutWeight'],
        });
      }
    });

    // Validate total duration doesn't exceed the maximum allowed monitored rollout duration
    const maxTotalDurationMs = maximumAllowedMonitoredRolloutDurationMs();
    if (maxTotalDurationMs > 0) {
      const totalDurationMs = steps.reduce((total, step) => total + getMsFromDuration(step.duration), 0);

      if (totalDurationMs > maxTotalDurationMs) {
        const maxTotalDurationDisplay = convertMsToDisplayDuration(maxTotalDurationMs);
        const totalDurationDisplay = convertMsToDisplayDuration(totalDurationMs);
        ctx.addIssue({
          code: 'custom',
          message: msg('guardedRollouts.stepValidation.totalDurationMax', {
            total: totalDurationDisplay,
            max: maxTotalDurationDisplay,
          }),
          path: [],
        });
      }
    }
  });
}

export const progressiveReleasePhaseConfigurationStepsSchema =
  createProgressiveReleasePhaseConfigurationStepsSchema('progressive');
export const guardedReleasePhaseConfigurationStepsSchema =
  createProgressiveReleasePhaseConfigurationStepsSchema('guarded');

export type ProgressiveReleasePhaseConfigurationSteps = z.infer<typeof progressiveReleasePhaseConfigurationStepsSchema>;

type NumberPair = [a: number, b: number];
const toNumberPairs = (arr: number[]) => arr.slice(1).map((e, i): NumberPair => [arr[i], e]);
